// Layer 4 (UI) — Process › Readings (locked comp `process.html` A): the
// operator's record-and-submit table with a 24 h control strip per charted
// reading, beside the selected reading's control chart (an 8/4 row). Below
// 640 px the table becomes a list with the out-of-range reading first.
// Renders Scene.transform.ts; derives nothing.

import { Commentable } from '@/shared/comments/Commentable';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { CARD_TABLE, HOT_ROW } from '../downtime/DowntimeCard';
import { CaseCodeChip } from '../quality/QualityUi';
import { ControlStrip } from './ControlStrip';
import { ProcessCard, ProcessChartBlock, StatusMark, SubLabel, ToneChip } from './ProcessUi';
import {
  RECORD_READING_TITLE,
  type CompactReadingRow,
  type HeldBy,
  type ReadingRow,
} from './Scene.transform';

const SELECTED_ROW = cn(HOT_ROW, '[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-accent-500)]');

export function ReadingsTab({
  caption,
  rows,
  compact,
  selectedId,
  onSelect,
  heldBy,
  onOpenCase,
}: {
  caption: string;
  rows: readonly ReadingRow[];
  /** The phone list: parameter, value · band subline at the cursor, status. */
  compact: readonly CompactReadingRow[];
  selectedId: string;
  onSelect: (id: string) => void;
  heldBy?: HeldBy;
  onOpenCase: (caseId: string, title: string) => void;
}) {
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];
  return (
    <Commentable name="Readings">
      <div className="grid min-w-0 gap-3 @[1040px]:grid-cols-12 @[1040px]:items-start" data-readings>
        <ProcessCard
          title="Equipment readings"
          caption={caption}
          className="@[1040px]:col-span-8"
          data-readings-table
          actions={
            <button
              type="button"
              disabled
              title={RECORD_READING_TITLE}
              className="inline-flex h-8 cursor-not-allowed items-center rounded-[10px] border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-3 text-sm font-medium text-ink-secondary opacity-60"
            >
              Record reading
            </button>
          }
        >
          <ul className="divide-y divide-[var(--color-divider)] sm:hidden">
            {compact.map((row) => (
              <li key={row.id} className="flex min-h-[44px] items-center justify-between gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-primary">{row.parameter}</p>
                  <p className="text-xs text-muted">{row.subline}</p>
                </div>
                <StatusMark status={row.status} tone={row.tone} />
              </li>
            ))}
          </ul>
          <div className={cn(CARD_TABLE, 'hidden sm:block')}>
            <Table wrap={false} className="tabular-nums">
              <THead>
                <TR>
                  <TH>Parameter</TH>
                  <TH className="text-right">Reading</TH>
                  <TH>Normal range</TH>
                  <TH>Status</TH>
                  <TH>Last 24 h · vs control limits</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR
                    key={row.id}
                    data-reading-row={row.id}
                    aria-selected={row.id === selected?.id}
                    onRowClick={() => onSelect(row.id)}
                    className={row.id === selected?.id ? SELECTED_ROW : undefined}
                  >
                    <TD className="font-semibold !text-ink-primary">{row.parameter}</TD>
                    <TD className="whitespace-nowrap text-right font-semibold !text-ink-primary">
                      {row.reading}
                      {row.basis ? (
                        <span className="text-xs font-normal text-muted" data-reading-basis={row.id}> · {row.basis}</span>
                      ) : null}
                    </TD>
                    <TD className="whitespace-nowrap">{row.range}</TD>
                    <TD><StatusMark status={row.status} tone={row.statusTone} /></TD>
                    <TD className="whitespace-nowrap">
                      {row.chart ? (
                        <span className="flex items-center gap-2" data-reading-strip={row.chart.variableId}>
                          <ControlStrip chart={row.chart} width={116} />
                          <span className={cn('text-xs', row.chart.beyond ? 'font-semibold text-[var(--state-blocked)]' : 'text-muted')}>
                            {row.chart.stateLabel}
                          </span>
                        </span>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        </ProcessCard>

        {selected ? (
          <ReadingDetail row={selected} heldBy={heldBy} onOpenCase={onOpenCase} className="@[1040px]:col-span-4" />
        ) : null}
      </div>
    </Commentable>
  );
}

function ReadingDetail({
  row,
  heldBy,
  onOpenCase,
  className,
}: {
  row: ReadingRow;
  heldBy?: HeldBy;
  onOpenCase: (caseId: string, title: string) => void;
  className?: string;
}) {
  if (!row.chart) {
    return (
      <ProcessCard title={row.parameter} className={className} data-reading-detail={row.id} actions={<StatusMark status={row.status} tone={row.statusTone} />}>
        <p className="text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">{row.reading}</p>
        <p className="mt-1 text-xs tabular-nums text-muted">
          {row.basis ? <span data-reading-basis={row.id}>{row.basis} · </span> : null}
          vs normal range {row.range}
        </p>
      </ProcessCard>
    );
  }
  return (
    <ProcessCard
      title={
        <span className="inline-flex items-baseline gap-1.5">
          {row.parameter}
          {row.tag ? <span className="text-xs font-normal text-muted">{row.tag}</span> : null}
          {/* A derived reading names its basis here too, so charting it does not
              lose the basis the Readings table carries (P17 r1, #704; #735). */}
          {row.basis ? (
            <span className="text-xs font-normal text-muted" data-reading-basis={row.id}>
              · {row.basis}
            </span>
          ) : null}
        </span>
      }
      className={className}
      data-reading-detail={row.id}
      actions={
        <span className="inline-flex items-baseline gap-1.5 tabular-nums">
          <span className="text-sm font-semibold text-ink-primary">{row.reading}</span>
          <span className="text-xs text-muted">vs {row.range}</span>
        </span>
      }
    >
      <ProcessChartBlock chart={row.chart} />
      {heldBy ? (
        <div className="mt-3 border-t border-[var(--hairline)] pt-2.5" data-held-by={heldBy.tag}>
          <SubLabel>Held by</SubLabel>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink-primary">{heldBy.tag} · {heldBy.name}</span>
            <ToneChip tone={heldBy.tone}>{heldBy.modeLabel}</ToneChip>
            {heldBy.ask ? (
              <CaseCodeChip code={heldBy.ask.caseCode} label={heldBy.ask.caseTitle} onOpen={() => onOpenCase(heldBy.ask!.caseId, heldBy.ask!.caseTitle)} />
            ) : null}
          </div>
        </div>
      ) : null}
    </ProcessCard>
  );
}
